import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Loader2, Eye, AlertCircle } from 'lucide-react';
import type { AgentAutogenSettingsRow } from '../types/agentAutogen';
import {
  fetchAgentAutogenSettings,
  previewAgentAutogenPrompt,
  saveAgentAutogenSettings,
} from '../utils/agentAutogenSettingsApi';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { parseAdminStaffSession } from '../utils/adminSession';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

const INPUT =
  'w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all';
const TEXTAREA = `${INPUT} resize-none font-mono text-sm`;

const emptyRow = (): AgentAutogenSettingsRow => ({
  id: 1,
  meta_system_prompt: null,
  default_extra_user_prompt: null,
  conversational_human_like: true,
  professional_formal: false,
  include_guardrails: true,
  strict_onboarding_privacy: true,
  extended_onboarding: false,
  user_prompt_template: null,
});

function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 rounded-lg border border-subtle p-3 transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-overlay/40'
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 rounded border-subtle"
      />
      <span>
        <span className="block text-sm font-medium text-th-primary">{label}</span>
        <span className="block text-xs text-th-secondary mt-0.5">{description}</span>
      </span>
    </label>
  );
}

export const AgentAutogenerateSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [builtInSample, setBuiltInSample] = useState<string | null>(null);
  const [form, setForm] = useState<AgentAutogenSettingsRow>(emptyRow);
  const [previewSample, setPreviewSample] = useState(
    'A coaching assistant that helps sales reps practice cold calls and track weekly goals.'
  );
  const [previewUser, setPreviewUser] = useState('');
  const [previewSystem, setPreviewSystem] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [migrationRequired, setMigrationRequired] = useState(false);
  const [schemaCacheStale, setSchemaCacheStale] = useState(false);
  const [settingsHint, setSettingsHint] = useState<string | null>(null);

  const parsedAccess = parseAdminStaffSession();
  const isSuperAdmin = parsedAccess?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/agents', { replace: true });
      return;
    }
    if (!parsedAccess?.hy_user_id?.trim()) {
      setError('Sign in with Supabase as a superadmin operator and reload this page.');
      setLoading(false);
      return;
    }
    void (async () => {
      setLoading(true);
      setError(null);
      const { ok, data, errorMessage } = await fetchAgentAutogenSettings();
      if (!ok) {
        setError(errorMessage || 'Failed to load settings');
        setLoading(false);
        return;
      }
      const d = data as {
        settings?: AgentAutogenSettingsRow | null;
        built_in_programmatic_prompt_sample?: string;
        migration_required?: boolean;
        schema_cache_stale?: boolean;
        hint?: string;
      };
      setMigrationRequired(!!d.migration_required);
      setSchemaCacheStale(!!d.schema_cache_stale);
      setSettingsHint(typeof d.hint === 'string' ? d.hint : null);
      if (d.settings && typeof d.settings === 'object') {
        setForm({
          ...emptyRow(),
          ...d.settings,
          id: 1,
        });
      }
      if (typeof d.built_in_programmatic_prompt_sample === 'string') {
        setBuiltInSample(d.built_in_programmatic_prompt_sample);
      }
      setLoading(false);
    })();
  }, [isSuperAdmin, navigate, parsedAccess?.hy_user_id]);

  const editDisabled = migrationRequired || schemaCacheStale;

  const handleSave = async () => {
    if (!parsedAccess?.hy_user_id?.trim() || editDisabled) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const { ok, data, errorMessage } = await saveAgentAutogenSettings({
      settings: form,
    });
    setSaving(false);
    if (!ok) {
      const d = data as { hint?: string; code?: string } | undefined;
      if (d?.code === 'schema_cache_stale') setSchemaCacheStale(true);
      if (d?.code === 'migration_required') setMigrationRequired(true);
      if (d?.hint) setSettingsHint(d.hint);
      setError([errorMessage, d?.hint].filter(Boolean).join(' — ') || 'Save failed');
      return;
    }
    setSuccess('Settings saved.');
    setMigrationRequired(false);
    setSchemaCacheStale(false);
    setSettingsHint(null);
  };

  const handlePreview = async () => {
    if (!parsedAccess?.hy_user_id?.trim()) return;
    setPreviewLoading(true);
    setError(null);
    const { ok, data, errorMessage } = await previewAgentAutogenPrompt({
      sampleAppDescription: previewSample.trim(),
    });
    setPreviewLoading(false);
    if (!ok) {
      setError(errorMessage || 'Preview failed');
      return;
    }
    const d = data as { user_prompt?: string; system_message?: string };
    setPreviewUser(d.user_prompt ?? '');
    setPreviewSystem(d.system_message ?? '');
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-base h-screen max-h-screen">
      <AdminPageToolbar
        maxWidthClass="max-w-4xl"
        title="Autogenerate settings"
        subtitle="Superadmin — controls defaults for agent template generation"
      >
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || editDisabled}
          className="button-primary inline-flex items-center gap-2 shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </AdminPageToolbar>

      <main className="flex-1 min-h-0 overflow-y-auto max-w-4xl w-full mx-auto px-4 py-6 space-y-8">
        {schemaCacheStale && (
          <div className="rounded-lg border border-status-warning/40 bg-status-warning/10 px-4 py-3 text-sm text-th-primary space-y-2">
            <p className="font-medium text-status-warning">API schema cache out of date</p>
            <p className="text-th-secondary">
              The table may already exist in Postgres, but PostgREST has not reloaded its schema yet. This is common
              right after a migration.
            </p>
            {settingsHint ? <p className="text-xs text-th-secondary font-mono whitespace-pre-wrap">{settingsHint}</p> : null}
            <p className="text-xs text-th-secondary">
              Or apply migration <code className="font-mono">20260330160000_postgrest_reload_notify.sql</code> once (
              <code className="font-mono">npx supabase db push</code>).
            </p>
          </div>
        )}
        {migrationRequired && (
          <div className="rounded-lg border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-th-primary space-y-2">
            <p className="font-medium text-status-error">Database migration required</p>
            <p className="text-th-secondary">
              The table <code className="text-xs font-mono">public.agent_autogen_settings</code> is not in this
              database yet.
            </p>
            {settingsHint ? <p className="text-xs text-th-secondary font-mono whitespace-pre-wrap">{settingsHint}</p> : null}
            <p className="text-xs text-th-secondary">
              CLI: <code className="font-mono">npx supabase db push</code> — or run the SQL file in the Supabase SQL
              editor.
            </p>
          </div>
        )}
        {error && (
          <div className="flex gap-2 rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <p className="text-sm text-status-success border border-status-success/30 rounded-lg px-3 py-2 bg-status-success/10">
            {success}
          </p>
        )}

        <section className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-th-primary">Default toggles</h2>
          <p className="text-sm text-th-secondary">
            Applied for every autogenerate unless a superadmin overrides per request. These shape how system_instructions,
            public_description, and guardrails are described to the JSON model.
          </p>
          <div className="space-y-2">
            <ToggleRow
              id="t-conv"
              label="Conversational / human-like tone"
              description="Natural dialogue guidance in the programmatic prompt."
              checked={form.conversational_human_like}
              onChange={(v) => setForm((f) => ({ ...f, conversational_human_like: v }))}
              disabled={editDisabled}
            />
            <ToggleRow
              id="t-prof"
              label="Professional / formal tone"
              description="Business-appropriate phrasing in the programmatic prompt."
              checked={form.professional_formal}
              onChange={(v) => setForm((f) => ({ ...f, professional_formal: v }))}
              disabled={editDisabled}
            />
            <ToggleRow
              id="t-guard"
              label="Include guardrails field"
              description="Require a non-empty guardrails string in generated JSON."
              checked={form.include_guardrails}
              onChange={(v) => setForm((f) => ({ ...f, include_guardrails: v }))}
              disabled={editDisabled}
            />
            <ToggleRow
              id="t-privacy"
              label="Strict onboarding privacy"
              description="Discourage lead-capture fields unless the app truly needs them."
              checked={form.strict_onboarding_privacy}
              onChange={(v) => setForm((f) => ({ ...f, strict_onboarding_privacy: v }))}
              disabled={editDisabled}
            />
            <ToggleRow
              id="t-ext"
              label="Extended onboarding form"
              description="Allow more sections and fields in the generated onboarding schema."
              checked={form.extended_onboarding}
              onChange={(v) => setForm((f) => ({ ...f, extended_onboarding: v }))}
              disabled={editDisabled}
            />
          </div>
        </section>

        <section className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-th-primary">Meta system message (JSON model)</h2>
          <p className="text-sm text-th-secondary">
            Optional override for the system line sent to the model that emits JSON. Leave empty to derive from toggles
            (recommended).
          </p>
          <textarea
            className={TEXTAREA}
            rows={4}
            placeholder="Leave empty for automatic meta prompt from toggles"
            value={form.meta_system_prompt ?? ''}
            disabled={editDisabled}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, meta_system_prompt: v === '' ? null : v }));
            }}
          />
        </section>

        <section className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-th-primary">Default extra user prompt</h2>
          <p className="text-sm text-th-secondary">
            Appended to the built-in programmatic prompt (or available as <code className="text-xs">{'{{EXTRA_USER_PROMPT}}'}</code> /{' '}
            <code className="text-xs">{'{{EXTRA_BLOCK}}'}</code> in a custom template).
          </p>
          <textarea
            className={TEXTAREA}
            rows={3}
            value={form.default_extra_user_prompt ?? ''}
            disabled={editDisabled}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, default_extra_user_prompt: v }));
            }}
          />
        </section>

        <section className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-th-primary">Custom user prompt template</h2>
          <p className="text-sm text-th-secondary">
            Leave empty to use the <strong className="text-th-primary">built-in programmatic prompt</strong> (see reference
            below). When set, this replaces it entirely. Placeholders:{' '}
            <code className="text-xs">{'{{APP_DESCRIPTION}}'}</code>,{' '}
            <code className="text-xs">{'{{EXTRA_USER_PROMPT}}'}</code> (text from default extra above or per-request
            override), <code className="text-xs">{'{{EXTRA_BLOCK}}'}</code> (formatted block when extra is non-empty).
          </p>
          <textarea
            className={TEXTAREA}
            rows={16}
            placeholder="Leave empty for built-in prompt. Or paste a full custom template…"
            value={form.user_prompt_template ?? ''}
            disabled={editDisabled}
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, user_prompt_template: v === '' ? null : v }));
            }}
          />
        </section>

        {builtInSample ? (
          <section className="card p-6 space-y-2">
            <h2 className="text-lg font-semibold text-th-primary">Built-in programmatic prompt (reference)</h2>
            <p className="text-xs text-th-secondary">
              Sample with placeholder app description <code className="text-xs">__SAMPLE_APP_DESCRIPTION__</code>. Used when
              custom template is empty.
            </p>
            <pre className="max-h-80 overflow-auto rounded-lg border border-subtle bg-elevated p-3 text-xs text-th-secondary whitespace-pre-wrap">
              {builtInSample}
            </pre>
          </section>
        ) : null}

        <section className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-th-primary">Live preview</h2>
          <p className="text-sm text-th-secondary">
            Uses saved DB settings when the table exists; otherwise shows the built-in defaults. After migrating, save
            edits then preview for an exact match to autogenerate.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px] space-y-1">
              <label className="text-xs text-th-secondary">Sample app description</label>
              <input
                className={INPUT}
                value={previewSample}
                onChange={(e) => setPreviewSample(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="button-secondary inline-flex items-center gap-2"
              onClick={() => void handlePreview()}
              disabled={previewLoading}
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Preview prompts
            </button>
          </div>
          {previewSystem ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-th-primary">System message</p>
              <pre className="max-h-48 overflow-auto rounded-lg border border-subtle bg-elevated p-3 text-xs whitespace-pre-wrap">
                {previewSystem}
              </pre>
            </div>
          ) : null}
          {previewUser ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-th-primary">User prompt</p>
              <pre className="max-h-96 overflow-auto rounded-lg border border-subtle bg-elevated p-3 text-xs whitespace-pre-wrap">
                {previewUser}
              </pre>
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
};
